// PestDashboard — route-first for a Pest Control tech. Reads
// /pest/recurring-accounts so demo tenants (which only seed the recurring
// book, not jobs) still light up with real data.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import { toast } from "sonner";
import {
  Bug, Sparkle, MapPin, Clock, ArrowRight, CheckCircle,
  Warning, House, Repeat,
} from "@phosphor-icons/react";

const STATUS_CHIP = {
  overdue:   { cls: "bg-red-100 text-red-800",         label: "OVERDUE" },
  this_week: { cls: "bg-amber-100 text-amber-800",     label: "THIS WEEK" },
  soon:      { cls: "bg-cyan-100 text-cyan-800",       label: "SOON" },
  future:    { cls: "bg-slate-100 text-slate-700",     label: "SCHEDULED" },
};

export default function PestDashboard() {
  const nav = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [counts, setCounts] = useState({ overdue: 0, this_week: 0, soon: 0, future: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/pest/recurring-accounts");
        setAccounts(r.data?.accounts || []);
        setCounts(r.data?.counts || {});
      } catch { toast.error("Could not load accounts"); }
      finally { setLoading(false); }
    })();
  }, []);

  const dueSoon = accounts.filter(a => ["overdue","this_week"].includes(a.status));
  const nextStop = accounts.find(a => a.status === "overdue") || accounts.find(a => a.status === "this_week") || accounts[0];
  const stops = accounts.filter(a => a.id !== nextStop?.id).slice(0, 4);
  const todayCount = (counts.overdue || 0) + (counts.this_week || 0);

  return (
    <AdminShell>
      <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-4" data-testid="pest-dashboard">
        {/* HERO — Today's route */}
        <div
          onClick={() => nav("/admin/pest/route-radar")}
          data-testid="pest-hero"
          className="cursor-pointer relative rounded-2xl overflow-hidden bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-950 p-6 sm:p-8 text-white hover:shadow-xl transition group"
        >
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-6 right-6"><Bug weight="fill" size={200}/></div>
          </div>
          <div className="relative">
            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-200 inline-flex items-center gap-1.5">
              <Sparkle weight="fill" size={11}/> Route Radar · Recurring service
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl leading-tight mt-2 tracking-tight" data-testid="pest-hero-title">
              {todayCount > 0 ? (
                <>{todayCount} stop{todayCount === 1 ? "" : "s"} today.<br/><span className="text-emerald-300">Let's clear the route.</span></>
              ) : (
                <>Route is clear.<br/><span className="text-emerald-300">Nothing due this week.</span></>
              )}
            </h1>
            <div className="text-sm text-emerald-100/80 mt-2 max-w-md">
              Every ticket auto-logs your pesticide, EPA reg, and target pest — state-compliant, no clipboard.
            </div>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-emerald-400 text-slate-900 font-black uppercase text-[11px] tracking-widest group-hover:scale-105 transition">
                <MapPin weight="bold" size={14}/> Open Route Radar
              </div>
              <button type="button" onClick={(e) => { e.stopPropagation(); nav("/admin/pest/pesticide-log"); }}
                data-testid="pest-hero-log"
                className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-white/10 border border-white/20 text-white font-black uppercase text-[11px] tracking-widest">
                <CheckCircle weight="bold" size={14}/> Pesticide Log
              </button>
            </div>
          </div>
        </div>

        {/* NEXT STOP */}
        {nextStop && (
          <div className="rounded-xl bg-white border-2 border-slate-200 overflow-hidden" data-testid="pest-next-stop">
            <div className="px-4 py-2 bg-slate-900 text-white flex items-center gap-2">
              <MapPin weight="fill" size={14}/>
              <div className="text-[10px] font-black uppercase tracking-widest">Next stop</div>
              <StatusChip s={nextStop.status}/>
              <span className="text-[9px] font-black uppercase tracking-widest bg-emerald-500 text-white px-1.5 py-0.5 rounded ml-auto inline-flex items-center gap-1">
                <Repeat weight="fill" size={9}/> {nextStop.cadence?.toUpperCase() || "RECURRING"}
              </span>
            </div>
            <div className="p-4">
              <div className="font-display font-black text-xl leading-tight" data-testid="next-stop-title">{nextStop.account_name}</div>
              <div className="text-sm text-slate-500 font-medium inline-flex items-center gap-1 mt-1">
                <House size={12}/> {nextStop.site_address} · target: {nextStop.default_target_pest || "—"}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <a href={`https://maps.google.com/?q=${encodeURIComponent(nextStop.site_address || "")}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="next-stop-nav"
                  className="h-10 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                  <MapPin weight="fill" size={13}/> Navigate
                </a>
                <button type="button" onClick={() => nav("/admin/pest/pesticide-log")}
                  data-testid="pest-log-btn"
                  className="h-10 px-3 rounded-lg bg-white border-2 border-slate-200 hover:border-slate-900 text-slate-800 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                  Log ticket <ArrowRight size={12}/>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* THIS WEEK'S ROUTE */}
        <div className="rounded-xl bg-white border-2 border-slate-200 overflow-hidden" data-testid="pest-today-stops">
          <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 inline-flex items-center gap-1.5">
              <Clock size={12}/> This week's route
            </div>
            <button type="button" onClick={() => nav("/admin/pest/route-radar")}
              data-testid="pest-all"
              className="text-[10px] font-black uppercase tracking-widest text-emerald-600 hover:text-emerald-800 inline-flex items-center gap-1">
              All accounts <ArrowRight size={10}/>
            </button>
          </div>
          {loading ? (
            <div className="p-6 text-sm text-slate-500 font-medium">Loading…</div>
          ) : stops.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 font-medium" data-testid="pest-no-stops">
              No other accounts due right now.
            </div>
          ) : (
            stops.map((a, i) => (
              <div key={a.id} className={`p-3 flex items-center gap-3 ${i > 0 ? "border-t border-slate-100" : ""}`}
                    data-testid={`pest-stop-${a.id}`}>
                <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                  <Bug weight="fill" size={16}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-sm truncate">{a.account_name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{a.site_address} · {a.default_target_pest}</div>
                </div>
                <div className="flex items-center gap-1.5">
                  <StatusChip s={a.status}/>
                  <span className="text-[9px] font-black uppercase tracking-widest bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5">
                    <Repeat weight="fill" size={8}/>
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* FUNNEL */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="pest-funnel">
          <FunnelTile label="Overdue" value={counts.overdue || 0} icon={<Warning size={14}/>} accent="red"/>
          <FunnelTile label="This week" value={counts.this_week || 0} icon={<Clock size={14}/>} accent="amber"/>
          <FunnelTile label="Coming soon" value={counts.soon || 0} icon={<Repeat size={14}/>} accent="emerald"/>
          <FunnelTile label="Scheduled" value={counts.future || 0} icon={<CheckCircle size={14}/>} accent="slate"/>
        </div>
      </div>
    </AdminShell>
  );
}

function StatusChip({ s }) {
  const m = STATUS_CHIP[s] || { cls: "bg-slate-100 text-slate-600", label: (s || "").toUpperCase() };
  return <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${m.cls}`}>{m.label}</span>;
}

function FunnelTile({ label, value, icon, accent }) {
  const bg = {
    red:     "bg-red-50 border-red-200 text-red-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    slate:   "bg-slate-50 border-slate-200 text-slate-800",
  }[accent] || "bg-slate-50 border-slate-200 text-slate-800";
  return (
    <div className={`rounded-xl border-2 p-3 ${bg}`}>
      <div className="text-[9px] font-black uppercase tracking-widest opacity-70 inline-flex items-center gap-1">{icon} {label}</div>
      <div className="font-display font-black text-2xl mt-0.5">{value}</div>
    </div>
  );
}
